import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Trash2, X, Loader2 } from "lucide-react";
import { caricaIndice, onIndice, cercaIndice, aggiornaIndice, svuotaIndice, normalizza, rigaIndice, filtraIndice } from "./indiceLocale";
import { ACCOUNTS, PEC_ACCOUNTS, socialNetwork } from "./mock";
import * as api from "./api";
import { API_URL } from "./config";
import TopBar from "./components/TopBar";
import EmailList from "./components/EmailList";
import EmailDetail from "./components/EmailDetail";
import BottomToolbar from "./components/BottomToolbar";
import { salvaHtml, salvaPdf } from "./esporta";
import { statoOutbox } from "./intestazione";
import Oscuramento from "./components/Oscuramento";
import ComposeModal from "./components/ComposeModal";
import AIPanel from "./components/AIPanel";
import AddAccountModal from "./components/AddAccountModal";
import DeleteAccountModal from "./components/DeleteAccountModal";
import ContextMenu from "./components/ContextMenu";
import AuthPage from "./components/AuthPage";
import SignatureModal from "./components/SignatureModal";
import SyncBar from "./components/SyncBar";
import VideoCallModal from "./components/VideoCallModal";
import VikiLoader from "./components/VikiLoader";
import AdminModal from "./components/AdminModal";
import ErroriModal from "./components/ErroriModal";
import ManutenzioneModal from "./components/ManutenzioneModal";
import RelayModal from "./components/RelayModal";
import QuickBar from "./components/QuickBar";
import CalendarioModal from "./components/CalendarioModal";
import MemoBanner from "./components/MemoBanner";
import MemoRapidoModal from "./components/MemoRapidoModal";
import FullscreenEmail from "./components/FullscreenEmail";
import { BACKEND_HOST } from "./config";
import * as pushApi from "./push";
import { aggiornaBadge } from "./badge";

const STORAGE_KEY = "mailapp_emails_v2";

// Messaggio chiaro per l'utente anche quando il proxy (Cloudflare/ingress) risponde con HTML o senza corpo
function messaggioErroreLista(e) {
  const res = e?.response;
  if (!res) return "Nessuna risposta dal server: controlla la connessione";
  const d = res.data?.detail;
  if (typeof d === "string" && d) return d;
  return `Impossibile aggiornare la posta (errore ${res.status}). Riprova tra poco`;
}

const PAGE_SIZE = 20;
const MAX_IN_MEMORY = 200;   // email del server tenute in memoria (le altre si riscaricano quando si aprono)
const MAX_FULL_BODIES = 30;  // corpi completi (HTML) tenuti in memoria oltre all'email aperta
const IDLE_MS = 10 * 60 * 1000;

// Alleggerisce un'email: via il corpo HTML, resta l'anteprima; verrà riscaricata all'apertura
function alleggerisci(e) {
  const { corpo_html, ...r } = e;
  return { ...r, corpo: (e.corpo || "").slice(0, 400), light: true };
}

// Tiene in memoria al massimo MAX_IN_MEMORY email del server e MAX_FULL_BODIES corpi completi (più l'email aperta).
// Ritorna la stessa lista se non c'è nulla da potare (nessun re-render).
function potaEmails(list, keepId, limitaNumero = true) {
  const server = list.filter((e) => e.id.includes("|"));
  const troppe = limitaNumero && server.length > MAX_IN_MEMORY;
  const corpi = server.filter((e) => e.light === false && e.id !== keepId).length;
  if (!troppe && corpi <= MAX_FULL_BODIES) return list;
  let kept = server;
  if (troppe) {
    const sorted = [...server].sort((a, b) => (b.ts || 0) - (a.ts || 0));
    kept = sorted.slice(0, MAX_IN_MEMORY);
    const sel = sorted.find((e) => e.id === keepId);
    if (sel && !kept.includes(sel)) kept.push(sel);
  }
  let pieni = 0;
  const out = kept.map((e) => {
    if (e.light !== false || e.id === keepId) return e;
    pieni += 1;
    return pieni > MAX_FULL_BODIES ? alleggerisci(e) : e;
  });
  return [...list.filter((e) => !e.id.includes("|")), ...out];
}

// Istante di un'email per l'ordinamento: ts del server, altrimenti data "GG.MM.AAAA" + ora "HH:MM" (bozze/locali)
function tsDi(e) {
  if (e.ts) return e.ts;
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(e.data || "");
  if (!m) return 0;
  const [hh, mm] = (e.ora || "00:00").split(":").map(Number);
  return new Date(+m[3], +m[2] - 1, +m[1], hh || 0, mm || 0).getTime() / 1000;
}
const isMobileToast = window.matchMedia("(max-width: 640px)").matches;

// Bozze salvate sul server, mostrate come email nella cartella Bozze
function draftRows(items) {
  return items.map((b) => {
    const d = new Date((b.ts || 0) * 1000);
    return {
      id: "draft:" + b.id,
      draft: b,
      mittente: "Bozza",
      email: b.a || "",
      a: b.a,
      oggetto: b.oggetto || "(senza oggetto)",
      corpo: [b.a ? "A: " + b.a : "", b.cc ? "Cc: " + b.cc : "", b.ccn ? "Ccn: " + b.ccn : ""].filter(Boolean).join("\n") + (b.a || b.cc || b.ccn ? "\n\n" : "") + (b.corpo || ""),
      data: d.toLocaleDateString("it-IT").replaceAll("/", "."),
      ora: d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }),
      ts: b.ts,
      folder: "bozze",
      account: b.account_id || "tutti",
      categoria: "account",
      letto: true,
      light: false,
      allegati: b.allegati || [],
    };
  });
}

// Categoria di una riga email in base al tipo di account che la invia
function categoriaAccount(acc) {
  if (acc.tipo === "pec") return "pec";
  if (acc.tipo === "social") return "social";
  return "account";
}

// Oggetto di una risposta: aggiunge "Re:" se manca
function oggettoRisposta(oggetto) {
  return (oggetto || "").toLowerCase().startsWith("re:") ? oggetto : "Re: " + oggetto;
}

// Messaggio di conferma dopo l'invio
function messaggioInviata(r) {
  if (r === "In uscita") return "Email salvata in In uscita";
  if (r === "social") return "Messaggio Messenger inviato";
  if (r === "duplicato") return "Email già inviata (non ripetuta)";
  return "Email inviata";
}

// Riga informativa della ricerca: risultati locali + stato del controllo sul server
function testoRicerca(n, pronta, serverSearch) {
  if (!pronta) return `${n} risultati`;
  if (serverSearch.stato === "in_corso") return `${n} risultati · controllo il server…`;
  if (serverSearch.importate) return `${n} risultati · ${serverSearch.importate} scaricate dal server`;
  return `${n} risultati`;
}

// Elementi della coda "In uscita" (invii falliti con riprova automatica) mostrati come email nella cartella uscita
function outboxRows(items) {
  return items.map((o) => {
    const d = new Date((o.ts || 0) * 1000);
    const stato = statoOutbox(o, "breve");
    return {
      id: "outbox:" + o.id,
      outbox: o,
      mittente: o.account_email,
      email: o.account_email,
      a: o.a,
      oggetto: `[${stato}] ${o.oggetto || "(senza oggetto)"}`,
      corpo: o.corpo || "",
      data: d.toLocaleDateString("it-IT").replaceAll("/", "."),
      ora: d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }),
      ts: o.ts,
      folder: "uscita",
      account: o.account_id,
      categoria: "account",
      letto: true,
      light: false,
      allegati: [],
    };
  });
}

// Evento "installa app" (Android/desktop) catturato appena disponibile
let installPromptEvent = null;
window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); installPromptEvent = e; });

function messaggioErroreInvio(e) {
  const res = e?.response;
  if (!res) return "Nessuna risposta dal server (connessione interrotta o timeout). Riprova pure: se l'email fosse già partita non verrà inviata due volte";
  const d = res.data?.detail;
  if (typeof d === "string" && d) return d;
  if (res.status === 413) return "Allegati troppo grandi per il server: riduci la dimensione e riprova";
  if ([502, 503, 504, 520, 521, 522, 524].includes(res.status)) return `Il server non ha risposto in tempo (errore ${res.status}). Riprova tra poco: se l'email fosse già partita non verrà inviata due volte`;
  return `Invio non riuscito: risposta non valida dal server (errore ${res.status}). Riprova`;
}


// Svuota cache del browser + service worker e ricarica (manuale dalle Impostazioni o automatico a nuova versione)
async function svuotaCacheERicarica(msg) {
  if (msg) toast(msg);
  try {
    if (window.caches) for (const k of await caches.keys()) await caches.delete(k);
    if (navigator.serviceWorker) for (const r of await navigator.serviceWorker.getRegistrations()) await r.unregister();
  } catch (e) {
    console.debug("pulizia cache/service worker non completata", e);
  }
  Object.keys(sessionStorage).forEach((k) => sessionStorage.removeItem(k));
  setTimeout(() => window.location.reload(), 400);
}

// Nuova versione dell'app sul server → svuota la cache e ricarica una volta sola
const VERSION_KEY = "mailapp_app_version";
function controllaVersione(v) {
  if (!v) return false;
  const nota = localStorage.getItem(VERSION_KEY);
  localStorage.setItem(VERSION_KEY, v);
  if (!nota || nota === v || sessionStorage.getItem("mailapp_ver_reload") === v) return false;
  sessionStorage.setItem("mailapp_ver_reload", v);
  svuotaCacheERicarica("Nuova versione: aggiorno l'app...");
  return true;
}

// Errori JavaScript non gestiti → registro "Info errori" (solo se c'è una sessione)
if (typeof window !== "undefined" && !window.__erroriHook) {
  window.__erroriHook = true;
  const segnala = (messaggio, dettaglio) => api.segnalaErrore({ messaggio: String(messaggio).slice(0, 500), dettaglio: String(dettaglio || "").slice(0, 2000), url: window.location.pathname });
  window.addEventListener("error", (e) => segnala(e.message || "Errore script", e.error?.stack || `${e.filename}:${e.lineno}`));
  window.addEventListener("unhandledrejection", (e) => segnala(e.reason?.message || String(e.reason || "Promise rifiutata"), e.reason?.stack));
}

// Cache dei messaggi completi fuori dallo stato React (max 80): aprire un'email precaricata è istantaneo
const DETAIL_CACHE = new Map();
function cacheDetail(id, full) {
  if (!full || full.provvisorio) return; // corpo non ancora disponibile (server in pausa): niente cache, si riprova alla prossima apertura
  DETAIL_CACHE.set(id, full);
  if (DETAIL_CACHE.size > 300) DETAIL_CACHE.delete(DETAIL_CACHE.keys().next().value);
  salvaInCacheSW(id, full);
}
// Il corpo precaricato finisce anche nella cache del service worker (stesso URL di /emails/detail): dopo una
// ricarica della pagina o offline l'apertura resta istantanea, senza aspettare il nuovo prefetch
let swCachePromise = null;
function salvaInCacheSW(id, full) {
  if (!("caches" in window) || !full) return;
  swCachePromise = swCachePromise || caches.open("postaai-emails-v1").catch(() => null);
  swCachePromise.then((c) => {
    if (!c) return;
    const url = `${API_URL}/emails/detail?id=${encodeURIComponent(id)}`;
    c.match(url).then((hit) => {
      if (!hit) c.put(url, new Response(JSON.stringify(full), { headers: { "Content-Type": "application/json" } })).catch(() => {});
    });
  });
}

// Immagini del corpo precaricate dal browser (stesso URL del proxy usato poi nell'iframe → già in cache al click)
const IMG_WARM = new Set();
function scaldaImmagini(full, max = 8) {
  const html = full?.corpo_html;
  if (!html || IMG_WARM.has(full.id)) return;
  IMG_WARM.add(full.id);
  const tok = encodeURIComponent(localStorage.getItem("mailapp_token_v1") || "");
  const urls = [...html.matchAll(/<img\b[^>]*?\ssrc\s*=\s*["']((?:https?:)?\/\/[^"']+)/gi)].map((m) => (m[1].startsWith("//") ? "https:" + m[1] : m[1]).replace(/&amp;/gi, "&").replace(/&#0*38;/g, "&"));
  [...new Set(urls)].slice(0, max).forEach((u) => {
    const img = new Image();
    img.src = `${API_URL}/img?url=${encodeURIComponent(u)}&token=${tok}`;
  });
}

// Testo completo di una email (anche se in lista c'è solo l'anteprima): corpo testuale o HTML convertito in testo
function htmlInTesto(html) {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  doc.querySelectorAll("style,script,head").forEach((n) => n.remove());
  doc.querySelectorAll("br").forEach((n) => n.replaceWith("\n"));
  doc.querySelectorAll("p,div,tr,li,h1,h2,h3,h4,blockquote").forEach((n) => n.append("\n"));
  return (doc.body?.textContent || "").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}
async function testoCompleto(em) {
  let full = em;
  if (em.light && em.id.includes("|")) {
    try {
      full = DETAIL_CACHE.get(em.id) || (await api.getEmailDetail(em.id).then((f) => (cacheDetail(em.id, f), f)));
    } catch (e) {
      console.debug("dettaglio non disponibile per l'indice locale", em.id, e);
    }
  }
  const corpo = (full.corpo || "").trim();
  return corpo.length >= 400 || !full.corpo_html ? corpo : htmlInTesto(full.corpo_html) || corpo;
}
// Destinatari di "Rispondi a tutti": tutti gli indirizzi della mail originale tranne quelli delle mie caselle
function altriDestinatari(em, accounts) {
  const miei = new Set((accounts || []).map((a) => (a.email || "").toLowerCase()));
  return [em.a, em.cc]
    .filter(Boolean)
    .join(",")
    .split(/[,;]/)
    .map((x) => x.trim())
    .filter((x) => x && !miei.has((x.match(/<([^>]+)>/)?.[1] || x).toLowerCase()) && (x.match(/<([^>]+)>/)?.[1] || x).toLowerCase() !== (em.email || "").toLowerCase())
    .join(", ");
}
// Citazione con tutta la comunicazione avvenuta (intestazioni + testo integrale, comprese le risposte precedenti)
async function citazione(em, titolo) {
  const righe = [
    `---------- ${titolo} ----------`,
    `Da: ${em.mittente && em.mittente !== em.email ? `${em.mittente} <${em.email}>` : em.email || ""}`,
    em.a ? `A: ${em.a}` : null,
    em.cc ? `Cc: ${em.cc}` : null,
    `Data: ${em.data || ""} ${em.ora || ""}`.trim(),
    `Oggetto: ${em.oggetto || ""}`,
  ].filter(Boolean);
  return "\n\n" + righe.join("\n") + "\n\n" + (await testoCompleto(em));
}

// Cache locale legata all'utente: la posta di un altro utente che ha usato questo browser non deve mai comparire
function loadEmails() {
  try {
    const uid = api.loadSession()?.id;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && uid) {
      const c = JSON.parse(raw);
      // cache salvate in passato: righe "complete" ma senza corpo_html → leggere (altrimenti il corpo resta testo grezzo)
      if (c && c.uid === uid && Array.isArray(c.emails)) return c.emails.map((e) => (e.corpo_html || e.light ? e : { ...e, light: true, provvisorio: undefined }));
    }
  } catch (e) {
    console.debug("cache locale email non leggibile, riparto dalla rete", e);
  }
  localStorage.removeItem(STORAGE_KEY);
  return [];
}

function MailApp() {
  const [user, setUser] = useState(api.loadSession);
  const [emails, setEmails] = useState(loadEmails);
  const [categoria, setCategoria] = useState("tutti"); // tutti | social
  const [socialFilter, setSocialFilter] = useState(null); // Facebook | Instagram | TikTok | LinkedIn
  const [folder, setFolder] = useState("ricevute");
  const [accountFilter, setAccountFilter] = useState("tutti");
  const [selectedId, setSelectedId] = useState(null);
  const [relayOpen, setRelayOpen] = useState(false);
  const relayHintRef = useRef(false);
  const [openMenu, setOpenMenu] = useState(null); // id tendina aperta
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Ricerca: mentre scrivi → ricerca nel DB (tutta la posta scaricata, tutti i campi); col tasto → anche sui server IMAP
  const [serverSearch, setServerSearch] = useState({ stato: "idle", trovate: 0, importate: 0, q: "", ids: null, db: 0, totale: 0, hasMore: false, offset: 0, tutto: false });
  const [searchPec, setSearchPec] = useState(false); // solo PEC (caselle pec.it, legalmail, arubapec, certifiedmail…)
  const [searchPecTipo, setSearchPecTipo] = useState(""); // consegna | accettazione | anomalia | certificata
  const optsRicerca = useMemo(() => ({ pec: searchPec, pecTipo: searchPec ? searchPecTipo : "" }), [searchPec, searchPecTipo]);
  const uniscoRisultati = useCallback((lista) => {
    setEmails((prev) => {
      const byId = new Map(prev.map((e) => [e.id, e]));
      lista.forEach((e) => {
        const p = byId.get(e.id);
        byId.set(e.id, p ? { ...e, ...(p.light === false ? { corpo: p.corpo, corpo_html: p.corpo_html, allegati: p.allegati, light: false } : {}), folder: p.folder === "eliminate" || p.folder === "rifiutate" ? p.folder : e.folder, letto: p.letto || e.letto, preferito: e.preferito ?? p.preferito } : e);
      });
      return Array.from(byId.values());
    });
  }, []);
  const serverBusyRef = useRef(false);
  const cercaSulServer = useCallback(async (qArg, silenzioso = false) => {
    const q = (typeof qArg === "string" ? qArg : query).trim();
    if (q.length < 2 || serverBusyRef.current) return;
    serverBusyRef.current = true;
    setServerSearch((s) => ({ ...s, stato: "in_corso", q }));
    try {
      const r = await api.searchEmails(q, 1, optsRicerca);
      uniscoRisultati(r.emails);
      setServerSearch((s) => ({ stato: "fatto", trovate: r.totale, importate: r.importate, q, ids: new Set([...(s.q === q && s.ids ? s.ids : []), ...r.emails.map((e) => e.id)]), db: r.totale, totale: r.totale, hasMore: r.has_more, offset: r.emails.length, tutto: true }));
      if (!silenzioso) (r.errors || []).forEach((m) => toast.error(m, { duration: 6000 }));
      if (r.importate > 0) toast.success(`${r.importate} email scaricate dal server`);
    } catch (e) {
      setServerSearch((s) => ({ ...s, stato: "idle", q }));
      if (!silenzioso) toast.error(messaggioErroreLista(e));
    } finally {
      serverBusyRef.current = false;
    }
  }, [query, uniscoRisultati, optsRicerca]);
  // Pochi risultati nel DB (es. testo di email vecchie, di cui è salvata solo l'intestazione) → ricerca IMAP automatica, una volta per testo cercato
  const autoServerRef = useRef("");
  // ricerca automatica nel DB (debounce 350 ms): i risultati entrano in lista e vengono mostrati anche se la
  // corrispondenza è nel corpo completo o nei destinatari, campi che il filtro locale non ha
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setServerSearch({ stato: "idle", trovate: 0, importate: 0, q: "", ids: null, db: 0, totale: 0, hasMore: false, offset: 0, tutto: false });
      return;
    }
    let attivo = true;
    const t = setTimeout(async () => {
      try {
        let totale;
        if (optsRicerca.pec) {
          const r = await api.searchEmails(q, 0, optsRicerca);
          if (!attivo) return;
          uniscoRisultati(r.emails);
          setServerSearch({ stato: "db", trovate: r.totale, importate: 0, q, ids: new Set(r.emails.map((e) => e.id)), db: r.totale, totale: r.totale, hasMore: r.has_more, offset: r.emails.length, tutto: false, tempoMs: r.tempo_ms });
          totale = r.totale;
        } else {
          // OVUNQUE (mittente, destinatari, cc, oggetto, corpo) in tutta la posta: il server manda solo gli id, le righe sono nell'indice locale
          const r = await api.searchIds(q);
          if (!attivo) return;
          setServerSearch({ stato: "db", trovate: r.totale, importate: 0, q, ids: new Set(r.ids), db: r.totale, totale: r.totale, hasMore: false, offset: 0, tutto: false, tempoMs: r.tempo_ms });
          totale = r.totale;
        }
        if (totale < 5 && q.length >= 3 && autoServerRef.current !== q) {
          autoServerRef.current = q;
          setTimeout(() => cercaSulServer(q, true), 400); // automatica e silenziosa (niente avvisi per caselle in pausa); l'utente potrebbe star ancora scrivendo
        }
      } catch (e) {
        console.debug("ricerca locale non riuscita", e);
      }
    }, 250);
    return () => {
      attivo = false;
      clearTimeout(t);
    };
  }, [query, uniscoRisultati, cercaSulServer, optsRicerca]);
  // Pagina successiva dei risultati recenti (20 per volta; dopo "Cerca sul server" tutta la posta)
  const [loadingRisultati, setLoadingRisultati] = useState(false);
  const caricaAltriRisultati = useCallback(async () => {
    const q = query.trim();
    if (!serverSearch.hasMore || loadingRisultati || serverSearch.q !== q) return;
    setLoadingRisultati(true);
    try {
      const r = await api.searchEmails(q, 0, { ...optsRicerca, offset: serverSearch.offset, archivio: serverSearch.tutto ? 2 : 0 });
      uniscoRisultati(r.emails);
      setServerSearch((s) => ({ ...s, ids: new Set([...(s.ids || []), ...r.emails.map((e) => e.id)]), totale: r.totale, db: r.totale, hasMore: r.has_more, offset: s.offset + r.emails.length }));
    } catch (e) {
      toast.error(messaggioErroreLista(e));
    } finally {
      setLoadingRisultati(false);
    }
  }, [query, serverSearch.hasMore, serverSearch.offset, serverSearch.q, serverSearch.tutto, loadingRisultati, optsRicerca, uniscoRisultati]);
  const ricercaPronta = serverSearch.stato !== "idle" && serverSearch.q === query.trim();
  const [dark, setDark] = useState(() => localStorage.getItem("mailapp_dark") === "1");
  const [composeOpen, setComposeOpen] = useState(false);
  const [videoCallOpen, setVideoCallOpen] = useState(false);
  const [calendarioOpen, setCalendarioOpen] = useState(false);
  const [memo, setMemo] = useState([]);
  const [memoDaEmail, setMemoDaEmail] = useState(null);
  const [videoCallInit, setVideoCallInit] = useState(null);
  const caricaMemo = useCallback(() => api.getMemo().then(setMemo).catch(() => {}), []);
  useEffect(() => {
    if (!user) { setMemo([]); return; }
    caricaMemo();
    const t = setInterval(caricaMemo, 5 * 60000);
    return () => clearInterval(t);
  }, [user, caricaMemo]);
  const [soloNonLette, setSoloNonLette] = useState(false);
  const [socialInArrivo, setSocialInArrivo] = useState(false);
  const [composeInitial, setComposeInitial] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiMode, setAiMode] = useState(null);
  const [customAccounts, setCustomAccounts] = useState([]);
  const [loadingMail, setLoadingMail] = useState(false);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [signaturesOpen, setSignaturesOpen] = useState(false);
  const [ctxEmail, setCtxEmail] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [checkedIds, setCheckedIds] = useState([]);

  const knownIdsRef = useRef(new Set());
  const accountIdsRef = useRef(null); // id delle caselle dell'utente loggato (null = non ancora noti)
  // Cambio utente (login di un altro profilo, sessione scaduta, logout): via TUTTA la posta dell'utente precedente
  const prevUidRef = useRef(api.loadSession()?.id || null);
  useEffect(() => {
    const uid = user?.id || null;
    if (uid === prevUidRef.current) return;
    prevUidRef.current = uid;
    localStorage.removeItem(STORAGE_KEY);
    DETAIL_CACHE.clear();
    api.invalidaDettaglio();
    knownIdsRef.current = new Set();
    accountIdsRef.current = null;
    offsetRef.current = 0;
    cursoreRef.current = null;
    svuotaIndice();
    setEmails([]);
    setCustomAccounts([]);
    setSelectedId(null);
    setAccountFilter("tutti");
  }, [user?.id]);
  // Indice locale di tutte le intestazioni (ricerca istantanea): caricato dopo l'avvio, aggiornato ogni 3 minuti
  const [indiceVer, setIndiceVer] = useState(0);
  useEffect(() => onIndice(setIndiceVer), []);
  useEffect(() => {
    if (!user?.id) return;
    const uid = user.id;
    const t0 = setTimeout(() => caricaIndice(uid), 1500);
    const t = setInterval(() => caricaIndice(uid), 3 * 60000);
    return () => {
      clearTimeout(t0);
      clearInterval(t);
    };
  }, [user?.id]);
  const syncBusyRef = useRef(false);
  const offsetRef = useRef(0);
  const cursoreRef = useRef(null);
  const lastLoadMoreRef = useRef(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [syncStatus, setSyncStatus] = useState([]);
  const [notifiche, setNotifiche] = useState("spente");

  // Service worker + stato notifiche; apertura email da click su notifica
  useEffect(() => {
    if (!user) return;
    pushApi.registraSW().then(() => pushApi.statoNotifiche().then(setNotifiche)).catch(() => {});
    const onMsg = (ev) => {
      if (ev.data?.tipo === "apri-email" && ev.data.id) setSelectedId(ev.data.id);
    };
    navigator.serviceWorker?.addEventListener("message", onMsg);
    const apri = new URLSearchParams(window.location.search).get("apri");
    if (apri) setSelectedId(apri);
    return () => navigator.serviceWorker?.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!user]);

  const toggleNotifiche = useCallback(async () => {
    try {
      if (notifiche === "attive") {
        await pushApi.disattivaNotifiche();
        setNotifiche("spente");
        toast("Notifiche disattivate");
      } else {
        await pushApi.attivaNotifiche();
        setNotifiche("attive");
        toast.success("Notifiche attivate: riceverai un avviso per le nuove email");
        pushApi.provaNotifica().catch(() => {});
      }
    } catch (e) {
      toast.error(e?.message || "Impossibile attivare le notifiche");
    }
  }, [notifiche]);

  // 1) Lista leggera subito dal DB (20 email, solo intestazioni + anteprima)
  const caricaLista = useCallback(async (silent = false, preloaded = null) => {
    if (!silent) setLoadingMail(true);
    try {
      const res = preloaded || (await api.getEmails("tutti", 0, PAGE_SIZE, false));
      offsetRef.current = res.offset || PAGE_SIZE;
      cursoreRef.current = res.cursore || null; // paginazione a cursore (ts+id dell'ultima email) al posto di skip
      setHasMore(!!res.has_more);
      const inbox = res.emails.filter((e) => e.folder === "ricevute");
      if (knownIdsRef.current.size > 0 && !syncBusyRef.current) {
        const nuove = inbox.filter((e) => !knownIdsRef.current.has(e.id) && !e.letto);
        nuove.slice(0, 3).forEach((n) =>
          toast(`Nuova email da ${n.mittente}`, { description: n.oggetto, duration: 6000 })
        );
      }
      inbox.forEach((e) => knownIdsRef.current.add(e.id));
      setEmails((prev) => {
        const prevById = new Map(prev.map((e) => [e.id, e]));
        const resIds = new Set(res.emails.map((r) => r.id));
        const oldest = {};
        res.emails.forEach((r) => {
          const k = r.account + "|" + r.folder;
          oldest[k] = Math.min(oldest[k] ?? Infinity, r.ts || 0);
        });
        const merged = res.emails.map((r) => {
          const p = prevById.get(r.id);
          if (!p) return r;
          return {
            ...r,
            // corpo completo già scaricato: non tornare alla versione leggera
            ...(p.light === false ? { corpo: p.corpo, corpo_html: p.corpo_html, allegati: p.allegati, light: false } : {}),
            folder: p.folder === "eliminate" || p.folder === "rifiutate" ? p.folder : r.folder,
            letto: p.letto || r.letto,
            preferito: r.preferito ?? p.preferito,
          };
        });
        const adesso = Date.now() / 1000;
        // solo email di caselle dell'utente loggato (mai residui di un altro profilo usato su questo browser)
        const mia = (e) => !accountIdsRef.current || !e.account || e.account === "tutti" || accountIdsRef.current.has(e.account);
        // le copie locali delle inviate spariscono appena la sync porta quella vera (o dopo 3 minuti)
        const locals = prev.filter((e) => mia(e) && !e.id.includes("|") && !(e.locale && (adesso - (e.ts || 0) > 180 || merged.some((m) => m.folder === "inviate" && m.oggetto === e.oggetto && Math.abs((m.ts || 0) - e.ts) < 600))));
        // Email più vecchie già caricate con "carica altre": restano in lista
        const older = prev.filter(
          (e) =>
            mia(e) &&
            e.id.includes("|") &&
            !resIds.has(e.id) &&
            ((e.ts || 0) < (oldest[e.account + "|" + e.folder] ?? Infinity) || e.folder === "eliminate" || e.folder === "rifiutate")
        );
        return [...locals.filter((e) => e.folder !== "uscita" && e.folder !== "bozze"), ...merged, ...older, ...outboxRows(res.outbox || []), ...draftRows(res.drafts || [])];
      });
      // il provider rifiuta l'IP del server (Aruba 554): una volta per sessione proponi il relay SMTP
      const bloccata = (res.outbox || []).find((o) => /554|rifiuta le connessioni/i.test(o.ultimo_errore || ""));
      if (bloccata && !relayHintRef.current) {
        relayHintRef.current = true;
        toast.warning(`${bloccata.account_email}: il server di posta rifiuta l'IP dell'app. Configura un server di invio alternativo (relay).`, {
          duration: 20000,
          action: { label: "Configura relay", onClick: () => setRelayOpen(true) },
        });
      }
      return true;
    } catch (e) {
      if (!silent) toast.error(messaggioErroreLista(e));
      return false;
    } finally {
      if (!silent) setLoadingMail(false);
    }
  }, []);

  // 2) Lista subito, poi controllo delle nuove email sul server in background e aggiornamento silenzioso
  const fetchMail = useCallback(async (showToast = false, silent = false) => {
    const ok = await caricaLista(silent);
    if (!ok) return;
    try {
      const r = await api.refreshEmails("tutti", silent && !showToast);
      if (r.nuove > 0) await caricaLista(true);
      if (!silent) {
        if (r.errors && r.errors.length > 0) r.errors.forEach((m) => toast.error(m, { duration: 6000 }));
        else if (showToast) toast.success(r.nuove > 0 ? `Posta aggiornata: ${r.nuove} nuove email` : "Posta aggiornata");
      }
    } catch (e) {
      if (showToast) toast.error(messaggioErroreLista(e));
    }
  }, [caricaLista]);

  // Aggiornamento immediato della cache dopo ogni operazione (lettura, eliminazione, invio, account)
  const aggiornaSubito = useCallback(() => caricaLista(true), [caricaLista]);

  // Carica le email più vecchie (tutta la posta, dall'inizio)
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    lastLoadMoreRef.current = Date.now();
    try {
      const res = await api.getEmails("tutti", offsetRef.current, PAGE_SIZE, false, cursoreRef.current);
      offsetRef.current = res.offset || offsetRef.current + PAGE_SIZE;
      if (res.cursore) cursoreRef.current = res.cursore;
      setHasMore(!!res.has_more);
      setEmails((prev) => {
        const ids = new Set(prev.map((e) => e.id));
        return [...prev, ...res.emails.filter((r) => !ids.has(r.id))];
      });
      if (!res.has_more) toast.success("Hai caricato tutta la posta");
    } catch (e) {
      toast.error("Impossibile caricare altre email");
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore]);
  // "carica altri" in fondo alla lista: altri risultati se c'è una ricerca, altre email in Ricevute/Inviate, niente altrove
  const caricaAltri = useMemo(() => {
    if (query.trim().length >= 2) return caricaAltriRisultati;
    return folder === "ricevute" || folder === "inviate" ? loadMore : null;
  }, [query, folder, caricaAltriRisultati, loadMore]);

  // Avvio: UNA sola richiesta (/bootstrap) al posto di 7 → profilo, account, preferenze, stato caselle, sync, backup, prima pagina
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const b = await api.bootstrap();
        api.saveSession(localStorage.getItem("mailapp_token_v1"), b.me);
        setUser(b.me);
        setCustomAccounts(b.accounts || []);
        accountIdsRef.current = new Set((b.accounts || []).map((a) => a.id));
        statoCountRef.current = (b.accounts || []).length;
        setStatoCaselle(b.stato_caselle || {});
        setSyncStatus(b.sync_status || []);
        setBackupAllowed(!!b.backup_allowed);
        if (b.versione) { appVersionRef.current = b.versione; setAppVersion(b.versione); }
        prefsCaricateRef.current = true;
        setDark(!!b.prefs?.dark);
        setSoloNonLette(!!b.prefs?.solo_non_lette);
        setSocialInArrivo(!!b.prefs?.social_in_arrivo);
        await caricaLista(false, b.emails);
        api.refreshEmails("tutti", true).catch(() => {});
      } catch (e) {
        prefsCaricateRef.current = true;
        fetchMail();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!user]);

  // Salva le preferenze sul server quando cambiano (solo dopo averle caricate)
  const prefsCaricateRef = useRef(false);
  useEffect(() => {
    if (!user || !prefsCaricateRef.current) return;
    api.putPrefs({ dark, solo_non_lette: soloNonLette, social_in_arrivo: socialInArrivo }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dark, soloNonLette, socialInArrivo]);

  // Avanzamento importazione: polling ogni 2s finché è in corso, poi ogni 20s
  useEffect(() => {
    if (!user) return;
    let t;
    let prevRunning = false;
    const poll = async () => {
      try {
        const st = await api.getSyncStatus();
        setSyncStatus(st);
        const running = st.some((s) => s.running);
        const incompleta = st.some((s) => s.phase !== "completato");
        syncBusyRef.current = running || incompleta;
        if (running || prevRunning) caricaLista(true);
        prevRunning = running;
        t = setTimeout(poll, running || incompleta ? 3000 : 20000);
      } catch (e) {
        t = setTimeout(poll, 10000);
      }
    };
    t = setTimeout(poll, 3000); // il primo stato arriva già dal bootstrap
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!user, caricaLista]);


  // Sessione scaduta segnalata dal server
  useEffect(() => {
    const h = () => {
      setUser(null);
      toast("Sessione scaduta, accedi di nuovo");
    };
    window.addEventListener("mailapp-logout", h);
    return () => window.removeEventListener("mailapp-logout", h);
  }, []);

  // Ping ogni secondo (scheda visibile): keep-alive + "c'è qualcosa di nuovo?" → la lista si ricarica solo se la firma cambia;
  // porta anche la versione dell'app per lo svuotamento automatico della cache
  const pingFirmaRef = useRef(null);
  const appVersionRef = useRef("");
  const lastActiveRef = useRef(Date.now());
  useEffect(() => {
    if (!user) return;
    let t;
    let fermo = false;
    const tick = async () => {
      if (fermo) return;
      if (!document.hidden) {
        try {
          const r = await api.ping();
          if (r.versione && r.versione !== appVersionRef.current) { appVersionRef.current = r.versione; setAppVersion(r.versione); }
          if (controllaVersione(r.versione)) return;
          if (pingFirmaRef.current !== null && r.firma !== pingFirmaRef.current) caricaLista(true);
          pingFirmaRef.current = r.firma;
        } catch (e) {
          console.debug("ping server saltato", e);
        }
      }
      // utente attivo negli ultimi 30 s → 1 s; altrimenti 5 s (meno traffico e batteria)
      const attivo = Date.now() - lastActiveRef.current < 30000;
      ultimoPing = Date.now();
      t = setTimeout(tick, attivo ? 1000 : 5000);
    };
    let ultimoPing = 0;
    // ritorno all'attività dopo una pausa: ping subito, senza aspettare i 5 s
    const sveglia = () => {
      if (Date.now() - ultimoPing > 1500) { clearTimeout(t); tick(); }
    };
    ["pointerdown", "keydown"].forEach((ev) => window.addEventListener(ev, sveglia, { passive: true }));
    tick();
    return () => {
      fermo = true;
      clearTimeout(t);
      ["pointerdown", "keydown"].forEach((ev) => window.removeEventListener(ev, sveglia));
    };
  }, [user, caricaLista]);

  // Il controllo IMAP dei nuovi messaggi lo fa il server ogni 60 s per tutti gli account (anche con app chiusa);
  // qui solo una richiesta di riserva ogni 3 minuti con la scheda visibile. Le novità arrivano subito dal ping.
  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => { if (!document.hidden) fetchMail(false, true); }, 180000);
    return () => clearInterval(t);
  }, [user, fetchMail]);

  // PWA / browser: proponi una sola volta l'attivazione delle notifiche push (serve un tocco dell'utente)
  useEffect(() => {
    if (!user || notifiche === "attive" || !pushApi.supportate()) return;
    if (window.Notification?.permission !== "default" || localStorage.getItem("mailapp_push_prompt")) return;
    const t = setTimeout(() => {
      localStorage.setItem("mailapp_push_prompt", "1");
      toast("Vuoi ricevere una notifica per le nuove email anche con l'app chiusa?", {
        duration: 15000,
        action: { label: "Attiva notifiche", onClick: () => toggleNotifiche() },
      });
    }, 6000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!user, notifiche]);

  // Limite memoria: max 200 email e 30 corpi completi (l'email aperta resta sempre). Il numero non viene
  // ridotto nei 10 minuti dopo un "carica altre", così chi sta leggendo la posta vecchia non la perde.
  useEffect(() => {
    const limitaNumero = Date.now() - lastLoadMoreRef.current > IDLE_MS;
    const next = potaEmails(emails, selectedId, limitaNumero);
    if (next === emails) return;
    if (limitaNumero && emails.filter((e) => e.id.includes("|")).length > MAX_IN_MEMORY) {
      offsetRef.current = Math.min(offsetRef.current, MAX_IN_MEMORY);
      setHasMore(true);
    }
    setEmails(next);
  }, [emails, selectedId]);

  // Inattività > 10 minuti (o ritorno sulla scheda): refresh silenzioso dello stato senza perdere
  // posizione ed email aperta; la lista viene ricostruita dal server e potata a 200 email.
  const refreshSilenzioso = useCallback(async () => {
    setEmails((prev) => potaEmails(prev, selectedId, true));
    offsetRef.current = Math.min(offsetRef.current, MAX_IN_MEMORY);
    lastLoadMoreRef.current = 0;
    api.getAccounts().then(setCustomAccounts).catch(() => {});
    await fetchMail(false, true);
  }, [fetchMail, selectedId]);
  useEffect(() => {
    if (!user) return;
    const attivita = () => {
      const ora = Date.now();
      const inattivo = ora - lastActiveRef.current > IDLE_MS;
      lastActiveRef.current = ora;
      if (inattivo) refreshSilenzioso();
    };
    const onVis = () => { if (document.visibilityState === "visible") attivita(); };
    const eventi = ["pointerdown", "keydown", "touchstart", "wheel"];
    eventi.forEach((ev) => window.addEventListener(ev, attivita, { passive: true }));
    document.addEventListener("visibilitychange", onVis);
    return () => {
      eventi.forEach((ev) => window.removeEventListener(ev, attivita));
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user, refreshSilenzioso]);

  // Backup: disponibile solo per l'amministratore (BACKUP_ADMIN_EMAILS nel backend)
  const [backupAllowed, setBackupAllowed] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [erroriOpen, setErroriOpen] = useState(false);
  const [manutenzioneOpen, setManutenzioneOpen] = useState(false);
  const [appVersion, setAppVersion] = useState("");
  // Stato connessione caselle (verde/rosso/grigio), aggiornato ogni 5 minuti; tocco sul pallino = ricontrolla subito
  const [statoCaselle, setStatoCaselle] = useState({});
  const statoCountRef = useRef(-1);
  useEffect(() => {
    if (!user) return;
    const load = () => api.getAccountsStato().then(setStatoCaselle).catch(() => {});
    if (statoCountRef.current !== customAccounts.length) {
      if (statoCountRef.current !== -1) load();
      statoCountRef.current = customAccounts.length;
    }
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [user, customAccounts.length]);
  const checkStato = useCallback((id) => {
    toast("Controllo la connessione...");
    api
      .checkAccountStato(id)
      .then((s) => {
        setStatoCaselle((prev) => ({ ...prev, [id]: s }));
        if (s.imap && s.smtp !== false) toast.success("Server di posta raggiungibile");
        else toast.error("Problema di connessione: " + (s.errore || ""));
      })
      .catch(() => toast.error("Controllo non riuscito"));
  }, []);
  // Riprova subito un'email in coda
  const retryOutbox = useCallback((o) => {
    toast.promise(
      api.retryOutbox(o.id).then((r) => { caricaLista(true); if (!r.sent) throw new Error(r.item?.ultimo_errore || "ancora non inviabile"); }),
      { loading: "Riprovo l'invio...", success: "Email inviata!", error: (e) => "Non ancora inviabile: " + (e?.message || "") }
    );
  }, [caricaLista]);
  // PWA: installazione
  const [installabile, setInstallabile] = useState(!!installPromptEvent);
  // Info server: per confrontare web e PWA (stesso server + stesso DB + stesso utente = stessi dati)
  const mostraDiagnostica = useCallback(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    api
      .diagnostica()
      .then((h) => {
        toast(`${standalone ? "PWA" : "Web"} · Server: ${BACKEND_HOST}`, {
          description: `DB: ${h.db_name} · Utente: ${user?.email} · Account: ${customAccounts.length} · Versione ${h.versione}`,
          duration: 15000,
        });
      })
      .catch(() => toast.error(`Server ${BACKEND_HOST} non raggiungibile`));
  }, [user, customAccounts.length]);
  useEffect(() => {
    const on = () => setInstallabile(true);
    window.addEventListener("beforeinstallprompt", on);
    return () => window.removeEventListener("beforeinstallprompt", on);
  }, []);
  // PWA installata dall'indirizzo di anteprima → dati diversi dal dominio pubblicato: avvisa una volta per sessione
  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    const anteprima = /\.preview\.emergentagent\.com$/i.test(window.location.hostname);
    if (!user || !standalone || !anteprima || sessionStorage.getItem("pwa_avviso_anteprima")) return;
    sessionStorage.setItem("pwa_avviso_anteprima", "1");
    toast.warning("PWA installata dall'indirizzo di ANTEPRIMA", {
      description: `Server: ${BACKEND_HOST}. Questo database è diverso da quello del dominio pubblicato: per vedere gli stessi dati del web, disinstalla questa app e reinstallala aprendo il dominio pubblicato nel browser.`,
      duration: 20000,
      action: { label: "Info server", onClick: () => mostraDiagnostica() },
    });
  }, [user, mostraDiagnostica]);
  const handleInstall = useCallback(async () => {
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) {
      toast.success("L'app è già installata");
      return;
    }
    if (installPromptEvent) {
      installPromptEvent.prompt();
      const r = await installPromptEvent.userChoice.catch(() => null);
      if (r?.outcome === "accepted") toast.success("App installata!");
      installPromptEvent = null;
      setInstallabile(false);
      return;
    }
    toast(isIOS ? "Su iPhone: tocca Condividi (quadrato con freccia) → \"Aggiungi alla schermata Home\"" : "Nel menu del browser scegli \"Installa app\" o \"Aggiungi a schermata Home\"", { duration: 9000 });
  }, []);

  const handleBackup = useCallback(() => {
    toast("Preparazione del backup in corso... il download partirà tra pochi secondi");
    window.location.assign(api.backupUrl());
  }, []);


  // Cache locale leggera: senza corpo HTML, al massimo 200 email, legata all'utente, scritta con 2 s di debounce
  useEffect(() => {
    if (!user?.id) return;
    const uid = user.id;
    const t = setTimeout(() => {
      try {
        // senza corpo HTML una riga NON è completa: torna leggera, così alla riapertura il corpo viene ripreso dalla cache/rete
        const light = emails.slice(0, MAX_IN_MEMORY).map(({ corpo_html, provvisorio, ...e }) => ({ ...e, corpo: (e.corpo || "").slice(0, 400), light: true }));
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ uid, emails: light }));
      } catch (e) {
        console.debug("cache locale email non salvata (spazio esaurito?)", e);
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [emails, user?.id]);

  // Caselle dell'utente: riferimento usato da caricaLista per scartare residui di altri profili
  useEffect(() => {
    if (customAccounts.length > 0) accountIdsRef.current = new Set(customAccounts.map((a) => a.id));
  }, [customAccounts]);

  useEffect(() => {
    localStorage.setItem("mailapp_dark", dark ? "1" : "0");
  }, [dark]);

  // Raggruppa le ricevute PEC (accettazione/consegna) sotto il messaggio originale
  const { hiddenIds, ricevuteDi } = useMemo(() => {
    const key = (e) => e.pec_msgid || (e.oggetto || "").trim().toLowerCase();
    const parents = new Map();
    emails.forEach((e) => {
      if (e.pec_tipo && !e.pec_ricevuta && !parents.has(key(e))) parents.set(key(e), e.id);
    });
    const hidden = new Set();
    const map = {};
    const lead = new Map();
    emails
      .filter((e) => e.pec_ricevuta && e.folder === "ricevute")
      .sort((a, b) => (a.ts || 0) - (b.ts || 0))
      .forEach((r) => {
        const k = key(r);
        let pid = parents.get(k);
        if (!pid) {
          if (!lead.has(k)) {
            lead.set(k, r.id);
            map[r.id] = [r];
            return;
          }
          pid = lead.get(k);
        }
        hidden.add(r.id);
        (map[pid] = map[pid] || []).push(r);
      });
    return { hiddenIds: hidden, ricevuteDi: map };
  }, [emails]);

  // Conteggio non lette per badge (PEC, social, account)
  const unread = useMemo(() => {
    const u = { pec: 0, pecVere: 0, social: 0, tutti: 0, byAccount: {}, bySocial: {} };
    const pecIds = new Set(customAccounts.filter((a) => a.tipo === "pec").map((a) => a.id));
    emails.forEach((e) => {
      if (e.letto || e.folder !== "ricevute" || hiddenIds.has(e.id)) return;
      const social = e.in_arrivo ? null : socialNetwork(e); // "Sposta in Posta in arrivo" toglie l'email dal Social
      u.byAccount[e.account] = (u.byAccount[e.account] || 0) + 1;
      if (social) {
        u.social += 1;
        u.bySocial[social] = (u.bySocial[social] || 0) + 1;
        return;
      }
      if (pecIds.has(e.account) || e.categoria === "pec") {
        u.pec += 1;
        if (!e.pec_ricevuta) u.pecVere += 1; // messaggi PEC veri, non le ricevute di accettazione/consegna
      }
      u.tutti += 1;
    });
    return u;
  }, [emails, hiddenIds, customAccounts]);

  // Non lette (email + social) su favicon, titolo della scheda e icona PWA; favicon rosso se c'è una PEC non letta
  useEffect(() => {
    aggiornaBadge(user ? unread.tutti + unread.social : 0, !!user && unread.pecVere > 0);
  }, [unread.tutti, unread.social, unread.pecVere, user]);

  const filtered = useMemo(() => {
    const q = query.trim();
    let base = emails;
    if (q.length >= 2 && !searchPec) {
      // ricerca istantanea: tutte le intestazioni dall'indice locale (nessuna attesa di rete), oltre alle email già in memoria
      const inStato = new Set(emails.map((e) => e.id));
      const extra = cercaIndice(q).filter((r) => !inStato.has(r.id));
      if (serverSearch.q === q && serverSearch.ids) {
        // trovate dal server nel corpo / destinatari completi: la riga viene dall'indice locale
        const gia = new Set(extra.map((r) => r.id));
        serverSearch.ids.forEach((id) => {
          if (inStato.has(id) || gia.has(id)) return;
          const r = rigaIndice(id);
          if (r) extra.push(r);
        });
      }
      if (extra.length) base = emails.concat(extra);
    } else if (folder === "inviate" || (accountFilter !== "tutti" && folder === "ricevute")) {
      // Inviate (es. PEC inviate) o una casella precisa: la lista in memoria copre solo le ultime pagine di TUTTA la
      // posta, quindi le email di quella cartella/casella arrivano dall'indice locale (tutte le intestazioni)
      const inStato = new Set(emails.map((e) => e.id));
      const extra = filtraIndice((r) => r.folder === folder && (accountFilter === "tutti" || r.account === accountFilter) && !inStato.has(r.id));
      if (extra.length) base = emails.concat(extra);
    }
    const termini = q ? normalizza(q).split(/\s+/).filter(Boolean) : [];
    return base.filter((e) => {
      if (hiddenIds.has(e.id)) return false;
      if (q) {
        // con una ricerca attiva si cerca OVUNQUE: ricevute e inviate insieme (Eliminate/Rifiutate solo se sei in quella cartella)
        const cestini = ["eliminate", "rifiutate"];
        if (cestini.includes(folder) ? e.folder !== folder : cestini.includes(e.folder)) return false;
      } else if (e.folder !== folder) return false;
      if (soloNonLette && e.letto) return false;
      const social = e.in_arrivo ? null : socialNetwork(e); // "Sposta in Posta in arrivo" toglie l'email dal Social
      if (categoria === "social") {
        if (!social) return false;
        if (socialFilter && social !== socialFilter) return false;
      } else if (social && folder === "ricevute" && !socialInArrivo && !q) {
        return false; // le email dei social stanno solo nella casella social
      }
      if (accountFilter !== "tutti" && e.account !== accountFilter) return false;
      if (q) {
        if (serverSearch.q === q && serverSearch.ids?.has(e.id)) return true; // trovata dal server (anche nel corpo completo)
        if (searchPec) return false; // solo PEC: decide il server (caselle/domini PEC e tipo di ricevuta)
        if (e.indice) return true; // già verificata dall'indice locale
        const testo = e._t || normalizza(`${e.mittente || ""}\n${e.email || ""}\n${e.oggetto || ""}\n${e.a || ""}\n${e.cc || ""}\n${e.corpo || ""}`);
        if (!termini.every((t) => testo.includes(t))) return false;
      }
      return true;
    }).sort((a, b) => tsDi(b) - tsDi(a)); // sempre dal più recente al più vecchio, in ogni categoria/cartella
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emails, hiddenIds, folder, categoria, socialFilter, accountFilter, query, soloNonLette, socialInArrivo, serverSearch.q, serverSearch.ids, searchPec, indiceVer]);

  // Apertura istantanea: i corpi delle email in lista vengono precaricati PROGRESSIVAMENTE (15 per richiesta, una
  // richiesta ogni 700 ms, fino a 120 email) in una cache fuori dallo stato React; le prime 15 anche con le immagini.
  // Le email con sola intestazione (vecchie, alleggerite) restano a carico dell'apertura.
  const prefetchBusyRef = useRef(false);
  const prefetchTentatiRef = useRef(new Set());
  const [prefetchGiro, setPrefetchGiro] = useState(0);
  useEffect(() => {
    if (!user || document.hidden) return;
    const t = setTimeout(async () => {
      if (prefetchBusyRef.current) return;
      const candidati = filtered.slice(0, 150).filter((e) => e.light && e.id.includes("|") && !e.solo_intestazione && !DETAIL_CACHE.has(e.id) && !prefetchTentatiRef.current.has(e.id));
      const ids = candidati.slice(0, 25).map((e) => e.id);
      if (ids.length === 0) return;
      prefetchBusyRef.current = true;
      ids.forEach((id) => prefetchTentatiRef.current.add(id));
      try {
        const r = await api.getEmailsDetails(ids);
        r.emails.forEach((full) => cacheDetail(full.id, full));
        if (prefetchTentatiRef.current.size <= 25) r.emails.slice(0, 25).forEach((full) => scaldaImmagini(full, 4));
      } catch (e) {
      } finally {
        prefetchBusyRef.current = false;
        if (candidati.length > ids.length) setPrefetchGiro((n) => n + 1); // prossimo blocco
      }
    }, prefetchGiro ? 350 : 250);
    return () => clearTimeout(t);
  }, [filtered, user, prefetchGiro]);

  // Pagine da 20 miste (ricevute+inviate): se la vista corrente resta corta, carica subito la pagina successiva
  useEffect(() => {
    if (!user || loadingMail || loadingMore || !hasMore || query) return;
    if (filtered.length < 10) loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length, hasMore, loadingMail, loadingMore]);

  const selected = useMemo(
    () => emails.find((e) => e.id === selectedId) || null,
    [emails, selectedId]
  );

  const emailsRef = useRef(emails);
  emailsRef.current = emails;
  const [fullscreen, setFullscreen] = useState(false);
  // Svuota definitivamente la cartella Eliminate (con conferma)
  const [confermaSvuota, setConfermaSvuota] = useState(false);
  const svuotaEliminate = useCallback(() => {
    setConfermaSvuota(false);
    const ids = emailsRef.current.filter((e) => e.folder === "eliminate").map((e) => e.id);
    setEmails((prev) => prev.filter((e) => e.folder !== "eliminate"));
    setSelectedId(null);
    api
      .purgeEliminate([])
      .then((r) => toast.success(`Cartella Eliminate svuotata (${r.eliminate ?? ids.length} email)`))
      .catch(() => toast.error("Svuotamento non riuscito sul server"));
  }, []);
  useEffect(() => {
    if (!selectedId) setFullscreen(false);
  }, [selectedId]);
  const chiudiFullscreen = useCallback(() => setFullscreen(false), []);

  // Prefetch al passaggio del mouse (o al tocco): il corpo si scarica PRIMA del click e finisce nella cache
  const prefetchHover = useCallback((id) => {
    if (!id || !id.includes("|")) return;
    const inCache = DETAIL_CACHE.get(id);
    if (inCache) return scaldaImmagini(inCache); // corpo già pronto: scaldo le immagini (intenzione di aprire)
    const em = emailsRef.current.find((e) => e.id === id);
    if (em && !em.light) return;
    api.getEmailDetail(id).then((full) => {
      cacheDetail(id, full);
      scaldaImmagini(full);
    }).catch(() => {});
  }, []);

  // Una riga che viene solo dall'indice locale (risultato di ricerca) entra nello stato prima di qualunque azione
  const adottaRiga = useCallback((id) => {
    if (emailsRef.current.some((e) => e.id === id)) return;
    const r = rigaIndice(id);
    if (!r) return;
    const { _c, _t, indice, ...pulita } = r;
    setEmails((prev) => (prev.some((e) => e.id === id) ? prev : [...prev, pulita]));
  }, []);
  const conRiga = useCallback((fn) => (em) => {
    if (em?.indice) adottaRiga(em.id);
    fn(em);
  }, [adottaRiga]);

  const handleSelect = useCallback((id) => {
    adottaRiga(id);
    const em = emailsRef.current.find((e) => e.id === id);
    if (em?.draft) {
      // Bozza: si riapre nel modulo Scrivi
      const b = em.draft;
      setComposeInitial({ draft_id: b.id, account_id: b.account_id, a: b.a, cc: b.cc, ccn: b.ccn, oggetto: b.oggetto, corpo: b.corpo, corpo_html: b.corpo_html || "", allegati: b.allegati || [] });
      setComposeOpen(true);
      return;
    }
    setSelectedId(id);
    setOpenMenu(null);
    aggiornaIndice(id, { letto: true });
    // corpo già in cache (prefetch o apertura precedente): entra nello stato nello stesso render dell'apertura → nessun flash
    const full = id.includes("|") ? DETAIL_CACHE.get(id) : null;
    setEmails((prev) => prev.map((e) => {
      if (e.id !== id) return e;
      if (full && e.light !== false) return { ...full, light: false, folder: e.folder, letto: true, preferito: e.preferito };
      if (e.provvisorio) return { ...e, provvisorio: undefined, letto: true }; // corpo provvisorio: si riscarica
      return { ...e, letto: true };
    }));
    if (id.includes("|")) api.markRead(id, true).catch(() => {}); // lo stato "letta" è già aggiornato in locale: niente ricaricamento della lista
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apre un'email per id anche se non è nella lista caricata (memo, notifiche): la scarica e la aggiunge
  const apriEmailId = useCallback(async (id) => {
    if (!id) return;
    const em = emailsRef.current.find((e) => e.id === id);
    if (em) {
      if (em.folder && em.folder !== folder) setFolder(em.folder);
      handleSelect(id);
      return;
    }
    try {
      const full = await api.getEmailDetail(id);
      cacheDetail(id, full);
      setEmails((prev) => (prev.some((e) => e.id === id) ? prev : [full, ...prev]));
      if (full.folder && full.folder !== folder) setFolder(full.folder);
      setSelectedId(id);
      setOpenMenu(null);
    } catch (e) {
      toast.error("Email non più disponibile");
    }
  }, [folder, handleSelect]);

  // Vista a tutto schermo (doppio click/tap in lista o tasto nella barra del dettaglio)

  // Messaggio completo (corpo HTML, allegati) per l'email aperta: vale per click in lista, notifiche push e link ?apri=
  const fetchingDetail = useRef(new Set());
  useEffect(() => {
    const id = selectedId;
    if (!id || !id.includes("|") || fetchingDetail.current.has(id)) return;
    const em = emails.find((e) => e.id === id);
    if (em && (em.light === false || em.corpo_html !== undefined || em.provvisorio)) return;
    fetchingDetail.current.add(id);
    const inCache = DETAIL_CACHE.get(id);
    (inCache ? Promise.resolve(inCache) : api.getEmailDetail(id).then((full) => (cacheDetail(id, full), full)))
      .then((full) =>
        setEmails((prev) => {
          if (full.provvisorio) {
            // corpo non ancora disponibile (server in pausa): resta "leggera" con il testo di avviso, si riprova alla prossima apertura
            return prev.map((e) => (e.id === id ? { ...e, provvisorio: full.corpo || true } : e));
          }
          return prev.some((e) => e.id === id)
            ? prev.map((e) => (e.id === id ? { ...full, light: false, folder: e.folder, letto: true, preferito: e.preferito } : e))
            : [...prev, { ...full, light: false, letto: true }];
        })
      )
      .catch(() => {
        setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, light: false } : e)));
        toast.error("Impossibile scaricare il messaggio completo");
      })
      .finally(() => fetchingDetail.current.delete(id));
  }, [selectedId, emails]);

  // Frecce su/giù: passa alla email precedente/successiva mentre ne leggi una
  const vaiA = useCallback(
    (delta) => {
      if (!selectedId) return;
      const idx = filtered.findIndex((e) => e.id === selectedId);
      const next = filtered[idx + delta];
      if (next) handleSelect(next.id);
    },
    [filtered, selectedId, handleSelect]
  );

  useEffect(() => {
    const onKey = (ev) => {
      if (!selectedId || composeOpen) return;
      const tag = (ev.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || ev.target?.isContentEditable) return;
      if (ev.key === "ArrowDown") {
        ev.preventDefault();
        vaiA(1);
      } else if (ev.key === "ArrowUp") {
        ev.preventDefault();
        vaiA(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, composeOpen, vaiA]);




  const handleDelete = useCallback(() => {
    if (!selected) {
      toast("Seleziona prima un'email");
      return;
    }
    if (selected.folder === "uscita" && selected.outbox) {
      api.deleteOutbox(selected.outbox.id).then(() => caricaLista(true)).catch(() => {});
      setEmails((prev) => prev.filter((e) => e.id !== selected.id));
      setSelectedId(null);
      toast.success("Email rimossa dalla coda");
      return;
    }
    if (selected.draft) {
      api.deleteDraft(selected.draft.id).catch(() => {});
      setEmails((prev) => prev.filter((e) => e.id !== selected.id));
      setSelectedId(null);
      toast.success("Bozza eliminata");
      return;
    }
    setEmails((prev) =>
      prev.map((e) => (e.id === selected.id ? { ...e, folder: "eliminate" } : e))
    );
    if (selected.id.includes("|")) api.setStato(selected.id, "eliminate").catch(() => {});
    setSelectedId(null);
    toast.success("Email spostata in Eliminate");
  }, [selected, caricaLista]);

  const handleSend = useCallback((mail) => {
    const fromAcc =
      customAccounts.find((a) => a.id === mail.account_id) ||
      customAccounts.find((a) => a.id === accountFilter) ||
      customAccounts[0];
    if (mail.bozza) {
      const payload = {
        id: mail.draft_id || "",
        account_id: fromAcc?.id || "",
        a: mail.a || "",
        cc: mail.cc || "",
        ccn: mail.ccn || "",
        oggetto: mail.oggetto || "",
        corpo: mail.corpo || "",
        corpo_html: mail.corpo_html || "",
        allegati: mail.allegati || [],
      };
      api
        .saveDraft(payload)
        .then((b) => {
          setEmails((prev) => [...draftRows([b]), ...prev.filter((e) => e.id !== "draft:" + b.id)]);
          toast.success("Bozza salvata sul server");
        })
        .catch((e) => toast.error(e?.response?.data?.detail || "Bozza non salvata"));
      setComposeOpen(false);
      setComposeInitial(null);
      return;
    }
    if (mail.draft_id) api.deleteDraft(mail.draft_id).then(() => setEmails((prev) => prev.filter((e) => e.id !== "draft:" + mail.draft_id))).catch(() => {});
    if (!fromAcc) {
      toast.error("Collega prima un account di posta per inviare");
      return;
    }
    setComposeOpen(false);
    setComposeInitial(null);
    toast.promise(
      api
        .preparaAllegati(
          (mail.allegati || []).map((al) => ({
            nome: al.nome,
            tipo: al.tipo || "",
            dati: al.dati || "",
            origine_id: al.origine?.id || "",
            origine_part: al.origine?.part ?? -1,
          }))
        )
        .then((allegati) =>
          api.sendEmail({
            account_id: fromAcc.id,
            a: mail.a,
            cc: mail.cc,
            ccn: mail.ccn,
            oggetto: mail.oggetto,
            corpo: mail.corpo,
            corpo_html: mail.corpo_html || "",
            allegati,
            client_id: mail.client_id || "",
            traccia: mail.traccia !== false,
          })
        )
        .then((r) => {
          if (r?.duplicato) {
            fetchMail();
            return "duplicato";
          }
          if (r?.queued) {
            fetchMail();
            toast.warning("Invio in corso in background", { description: r.detail, duration: 8000 });
            setFolder("uscita");
            return "In uscita";
          }
          // Mostra subito la mail tra le Inviate (la sync la sostituirà con la copia del server)
          const now = new Date();
          setEmails((prev) => [
            {
              id: "sent_" + now.getTime(),
              mittente: fromAcc.email,
              email: mail.a,
              a: mail.a,
              oggetto: mail.oggetto || "(senza oggetto)",
              corpo: mail.corpo || "",
              corpo_html: "",
              data: now.toLocaleDateString("it-IT").replaceAll("/", "."),
              ora: now.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }),
              ts: now.getTime() / 1000,
              folder: "inviate",
              account: fromAcc.id,
              categoria: categoriaAccount(fromAcc),
              letto: true,
              light: false,
              locale: true,
              allegati: (mail.allegati || []).map((f) => ({ nome: f.nome, tipo: f.tipo || "file", dimensione: f.dimensione || "" })),
            },
            ...prev,
          ]);
          setTimeout(() => fetchMail(false, true), 4000);
          return r?.social ? "social" : "ok";
        })
        .catch((e) => {
          // L'email non è partita: riapri il modulo con il testo così non va perso (non se l'invio è già in corso)
          if (e?.response?.status === 409) throw e;
          setComposeInitial({ a: mail.a, cc: mail.cc, ccn: mail.ccn, oggetto: mail.oggetto, corpo: mail.corpo, corpo_html: mail.corpo_html || "", allegati: mail.allegati || [], client_id: mail.client_id || "" });
          setComposeOpen(true);
          throw e;
        }),
      {
        loading: "Invio in corso da " + (fromAcc.tipo === "social" ? fromAcc.label : fromAcc.email) + "...",
        success: messaggioInviata,
        error: (e) => messaggioErroreInvio(e),
      }
    );
  }, [customAccounts, accountFilter, fetchMail]);

  // Risposta rapida dal dettaglio (pannello o tutto schermo)
  const quickReply = useCallback(
    (testo) => {
      if (!selected) return;
      handleSend({
        account_id: selected.account,
        a: selected.email,
        cc: "",
        ccn: "",
        oggetto: (selected.oggetto || "").toLowerCase().startsWith("re:") ? selected.oggetto : "Re: " + selected.oggetto,
        corpo: testo,
        allegati: [],
      });
    },
    [selected, handleSend]
  );

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // Salva l'email aperta come file HTML (immagini incorporate) o PDF (resa visiva) — nome: mittente + data
  const handleSave = useCallback(
    (tipo) => {
      if (!selected) { toast("Apri un'email per salvarla"); return; }
      const p = tipo === "pdf" ? salvaPdf(selected) : salvaHtml(selected);
      toast.promise(p, {
        loading: tipo === "pdf" ? "Creazione del PDF..." : "Preparazione del file HTML...",
        success: tipo === "pdf" ? "PDF scaricato" : "File HTML scaricato",
        error: (e) => "Salvataggio non riuscito: " + (e?.message || "riprova"),
      });
    },
    [selected]
  );

  const handleResetDemo = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setEmails([]);
    setSelectedId(null);
    fetchMail(true);
  }, [fetchMail]);

  // Azioni tendina "scegli rispondi"
  const handleReplyAction = useCallback(
    (action) => {
      if (!selected) {
        toast("Apri prima un'email");
        return;
      }
      if (action === "archivia") {
        toast.success("Email archiviata (demo)");
        return;
      }
      if (action === "inoltra") {
        (async () => {
          setComposeInitial({
            a: "",
            oggetto: "Fwd: " + selected.oggetto,
            corpo: "",
            citazione: (await citazione(selected, "Messaggio inoltrato")).trim(),
            // Allegati originali: inviati dal server prendendoli dal messaggio ricevuto
            allegati: (selected.allegati || []).map((al) => ({
              nome: al.nome,
              dimensione: al.dimensione,
              tipo: al.tipo,
              origine: { id: selected.id, part: al.part },
            })),
          });
          setComposeOpen(true);
        })();
        return;
      }
      // rispondi / rispondi_tutti (dallo stesso account che ha ricevuto il messaggio; Messenger: solo testo, senza citazione)
      const social = selected.categoria === "social";
      (async () => {
        setComposeInitial({
          account_id: selected.account,
          a: selected.email,
          cc: action === "rispondi_tutti" ? altriDestinatari(selected, customAccounts) : "",
          oggetto: social ? selected.oggetto : oggettoRisposta(selected.oggetto),
          corpo: "",
          citazione: social ? "" : (await citazione(selected, `Il ${selected.data} ${selected.mittente} ha scritto`)).trim(),
        });
        setComposeOpen(true);
      })();
    },
    [selected, customAccounts]
  );

  // Azioni tendina AI
  const handleAIAction = useCallback(
    (mode) => {
      if (!selected) {
        toast("Apri prima un'email per usare l'AI");
        return;
      }
      setAiMode(mode);
      setAiOpen(true);
    },
    [selected]
  );

  // Sposta un'email nel cestino (anche sul server, se remota)
  const moveToTrash = useCallback((id) => {
    setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, folder: "eliminate" } : e)));
    setSelectedId((sid) => (sid === id ? null : sid));
    if (id.includes("|")) api.deleteRemote(id).then(aggiornaSubito).catch(() => {});
    toast.success("Email spostata in Eliminate");
  }, [aggiornaSubito]);

  // Swipe destra: letta / non letta
  const handleSwipeUnread = useCallback((em) => {
    setEmails((prev) => prev.map((e) => (e.id === em.id ? { ...e, letto: !em.letto } : e)));
    if (em.id.includes("|")) api.markRead(em.id, !em.letto).then(aggiornaSubito).catch(() => {});
    toast.success(em.letto ? "Segnata come non letta" : "Segnata come letta");
  }, [aggiornaSubito]);

  // Swipe sinistra (prima meta'): archivia
  const handleSwipeArchive = useCallback((em) => {
    toast.success(`"${em.oggetto}" archiviata (demo)`);
  }, []);

  // Swipe sinistra (oltre meta'): elimina
  const handleSwipeDelete = useCallback((em) => {
    moveToTrash(em.id);
  }, [moveToTrash]);

  // Preferiti (salvati sul server: uguali su web e PWA)
  const toggleFavorite = useCallback((id, era) => {
    setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, preferito: !e.preferito } : e)));
    if (id.includes("|")) api.starEmail(id, !era).catch(() => {});
    toast.success(era ? "Rimossa dai preferiti" : "Aggiunta ai preferiti");
  }, []);

  // Risposta da un'email specifica
  const customAccountsRef = useRef(customAccounts);
  customAccountsRef.current = customAccounts;
  const openReply = useCallback(async (em, tutti) => {
    setComposeInitial({
      account_id: em.account,
      a: em.email,
      cc: tutti ? altriDestinatari(em, customAccountsRef.current) : "",
      oggetto: (em.oggetto || "").toLowerCase().startsWith("re:") ? em.oggetto : "Re: " + em.oggetto,
      corpo: "",
      citazione: (await citazione(em, `Il ${em.data} ${em.mittente} ha scritto`)).trim(),
    });
    setComposeOpen(true);
  }, []);

  // Azioni del popup pressione lunga
  const handleCtxAction = useCallback(
    (action) => {
      const em = ctxEmail;
      setCtxEmail(null);
      if (!em) return;
      switch (action) {
        case "elimina":
          if (em.folder === "eliminate") {
            setEmails((prev) => prev.filter((e) => e.id !== em.id));
            if (em.id.includes("|")) api.purgeEliminate([em.id]).then(() => toast.success("Eliminata definitivamente")).catch(() => toast.error("Eliminazione non riuscita"));
          } else moveToTrash(em.id);
          break;
        case "seleziona":
          setSelectMode(true);
          setCheckedIds([em.id]);
          break;
        case "stampa":
          handleSelect(em.id);
          toast("Preparazione stampa...");
          setTimeout(() => window.print(), 500);
          break;
        case "archivia":
          toast.success(`"${em.oggetto}" archiviata (demo)`);
          break;
        case "rispondi":
          openReply(em, false);
          break;
        case "rispondi_tutti":
          openReply(em, true);
          break;
        case "ripristina": {
          const addr = (em.email || "").toLowerCase();
          if (em.folder === "rifiutate" && addr.includes("@")) {
            // dalle Rifiutate: tornano in Ricevute TUTTE le email dello stesso mittente e la regola diventa "accetta"
            setEmails((prev) =>
              prev.map((e) => ((e.email || "").toLowerCase() === addr && e.folder === "rifiutate" ? { ...e, folder: "ricevute", in_arrivo: true } : e))
            );
            api
              .regolaMittente(addr, "accetta")
              .then((r) => toast.success(`Mittente ${addr} ripristinato: ${r.aggiornate} email tornate in Ricevute (anche le prossime)`))
              .catch(() => toast.error("Ripristino non salvato sul server"));
            break;
          }
          const orig = em.id.includes("|") ? em.id.split("|")[1] : "ricevute";
          setEmails((prev) => prev.map((e) => (e.id === em.id ? { ...e, folder: orig } : e)));
          if (em.id.includes("|")) api.setStato(em.id, "").catch(() => toast.error("Ripristino non salvato sul server"));
          toast.success("Email ripristinata in " + (orig === "inviate" ? "Inviate" : "Ricevute"));
          break;
        }
        case "in_arrivo":
          setEmails((prev) => prev.map((e) => (e.id === em.id ? { ...e, in_arrivo: true, folder: "ricevute" } : e)));
          if (em.id.includes("|")) api.moveInbox(em.id, true).catch(() => toast.error("Spostamento non salvato sul server"));
          setCategoria("tutti");
          setSocialFilter(null);
          setFolder("ricevute");
          toast.success("Email spostata in Posta in arrivo");
          break;
        case "preferiti":
          toggleFavorite(em.id, em.preferito);
          break;
        default:
          break;
      }
    },
    [ctxEmail, moveToTrash, handleSelect, openReply, toggleFavorite]
  );

  // Selezione multipla
  const toggleCheck = useCallback((id) => {
    setCheckedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setCheckedIds([]);
  }, []);

  const deleteChecked = useCallback(() => {
    setEmails((prev) => prev.map((e) => (checkedIds.includes(e.id) ? { ...e, folder: "eliminate" } : e)));
    checkedIds.forEach((id) => {
      if (id.includes("|")) api.deleteRemote(id).then(aggiornaSubito).catch(() => {});
    });
    toast.success(`${checkedIds.length} email eliminate`);
    exitSelectMode();
  }, [checkedIds, exitSelectMode, aggiornaSubito]);

  // Account collegato con successo dal backend
  const handleAddAccount = useCallback((acc) => {
    setCustomAccounts((prev) => [...prev, acc]);
    setAddAccountOpen(false);
    fetchMail();
  }, [fetchMail]);

  // Elimina un account di posta: definitivo sul server (email, allegati, coda, sync) e pulizia completa in app
  const handleDeleteAccount = useCallback((id) => {
    setCustomAccounts((prev) => prev.filter((a) => a.id !== id));
    setEmails((prev) => prev.filter((e) => e.account !== id));
    setSelectedId((s) => (s && String(s).startsWith(id + "|") ? null : s));
    setAccountFilter((f) => (f === id ? "tutti" : f));
    setStatoCaselle((prev) => { const n = { ...prev }; delete n[id]; return n; });
    knownIdsRef.current = new Set([...knownIdsRef.current].filter((x) => !String(x).startsWith(id + "|")));
    toast.promise(
      api.deleteAccount(id).then(() => caricaLista(true)),
      { loading: "Elimino l'account e la sua posta...", success: "Account eliminato definitivamente", error: (e) => messaggioErroreLista(e) }
    );
  }, [caricaLista]);

  // Elimina il profilo di accesso (utente registrato) e i suoi account collegati
  const handleDeleteProfile = useCallback(() => {
    api.deleteMe().catch(() => {});
    setDeleteAccountOpen(false);
    api.clearSession();
    setUser(null);
    setCustomAccounts([]);
    setEmails([]);
    toast.success("Profilo eliminato");
  }, []);

  const allAccounts = useMemo(
    () => [...ACCOUNTS, ...customAccounts.filter((a) => a.tipo !== "pec" && a.tipo !== "social")],
    [customAccounts]
  );
  const allPecAccounts = useMemo(
    () => [...PEC_ACCOUNTS, ...customAccounts.filter((a) => a.tipo === "pec")],
    [customAccounts]
  );
  const socialAccounts = useMemo(() => customAccounts.filter((a) => a.tipo === "social"), [customAccounts]);
  const mailAccounts = useMemo(() => customAccounts.filter((a) => a.tipo !== "social"), [customAccounts]);
  const [addAccountPre, setAddAccountPre] = useState(null);

  // Accetta / Rifiuta email aperta
  // Accetta / Rifiuta valgono per il MITTENTE: tutte le sue email (presenti e future) restano in Ricevute o vanno in Rifiutate
  const handleAcceptMail = useCallback(() => {
    if (!selected) return;
    const addr = (selected.email || "").toLowerCase();
    if (!addr.includes("@")) return;
    setEmails((prev) =>
      prev.map((e) => ((e.email || "").toLowerCase() === addr && e.folder !== "eliminate" && (e.folder === "rifiutate" || e.folder === "ricevute") ? { ...e, folder: "ricevute", in_arrivo: true } : e))
    );
    api
      .regolaMittente(addr, "accetta")
      .then((r) => toast.success(`Mittente ${addr} accettato: le sue email restano in Ricevute (${r.aggiornate} aggiornate)`))
      .catch(() => toast.error("Regola non salvata sul server"));
  }, [selected]);

  const handleRejectMail = useCallback(() => {
    if (!selected) return;
    const addr = (selected.email || "").toLowerCase();
    setEmails((prev) =>
      prev.map((e) =>
        e.id === selected.id || (addr.includes("@") && (e.email || "").toLowerCase() === addr && e.folder === "ricevute") ? { ...e, folder: "rifiutate" } : e
      )
    );
    if (addr.includes("@")) {
      api
        .regolaMittente(addr, "rifiuta")
        .then((r) => toast.success(`Mittente ${addr} rifiutato: ${r.aggiornate} email spostate in Rifiutate, anche le prossime`))
        .catch(() => toast.error("Regola non salvata sul server"));
    } else if (selected.id.includes("|")) api.setStato(selected.id, "rifiutate").catch(() => {});
    setSelectedId(null);
  }, [selected]);

  // Login / Logout (autenticazione reale, token JWT salvato nel browser)
  const handleLogin = useCallback((u) => {
    setUser(u);
  }, []);

  // SSO PAQ: senza sessione locale prova il cookie di .areapaq.it prima di mostrare il login
  const [ssoChecking, setSsoChecking] = useState(() => !api.loadSession());
  useEffect(() => {
    if (user || !ssoChecking) return;
    api
      .ssoSession()
      .then((r) => {
        if (r?.access_token && r.user) {
          api.saveSession(r.access_token, r.user);
          setUser(r.user);
        }
      })
      .catch(() => {})
      .finally(() => setSsoChecking(false));
  }, [user, ssoChecking]);

  const handleLogout = useCallback(() => {
    api.clearSession();
    setUser(null);
    setCustomAccounts([]);
    setEmails([]);
    toast("Sei uscito dall'account");
  }, []);

  const toolbarCommon = {
    openMenu,
    setOpenMenu,
    onTrash: handleDelete,
    onPrint: handlePrint,
    onReplyAction: handleReplyAction,
    onAIAction: handleAIAction,
  };
  const detailToolbar = {
    onSave: handleSave,
    onTranslate: () => {
      if (!selected) { toast("Apri un'email per tradurla"); return; }
      setAiMode("traduci");
      setAiOpen(true);
    },
  };

  return (
    <div className={`mail-app ${dark ? "dark-mode" : ""}`} data-testid="mail-app">
      <Toaster position={isMobileToast ? "top-center" : "bottom-center"} mobileOffset={{ top: 76, left: 12, right: 12 }} richColors />
      {/* su mobile i toast stanno in alto (sotto la barra): in basso coprivano i tasti Invia/Bozza del modulo Scrivi */}
      {!user && ssoChecking && <div className="sso-wait" data-testid="sso-checking">Verifica accesso AREAPAQ...</div>}
      {!user && !ssoChecking && <AuthPage onLogin={handleLogin} />}
      {user && (
      <>
      <div className={`app-shell ${selected ? "has-selection" : ""}`}>
        {openMenu && <Oscuramento menu={openMenu} onClick={() => setOpenMenu(null)} testid="dd-backdrop" />}

        <div className="pane pane-list">
          <TopBar
            mode="list"
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            folder={folder}
            setFolder={(f) => {
              setFolder(f);
              setSelectedId(null);
            }}
            accountFilter={accountFilter}
            setAccountFilter={(a) => {
              setAccountFilter(a);
              setSelectedId(null);
            }}
            categoria={categoria}
            setCategoria={(c) => {
              setCategoria(c);
              setSelectedId(null);
            }}
            socialFilter={socialFilter}
            setSocialFilter={setSocialFilter}
            searchOpen={searchOpen}
            setSearchOpen={setSearchOpen}
            query={query}
            setQuery={setQuery}
            onCompose={() => {
              setComposeInitial(null);
              setComposeOpen(true);
            }}
            dark={dark}
            toggleDark={() => setDark((d) => !d)}
            onResetDemo={handleResetDemo}
            accounts={allAccounts}
            pecAccounts={allPecAccounts}
            socialAccounts={socialAccounts}
            onAddAccount={(pre) => { setAddAccountPre(pre === "facebook" ? "facebook" : null); setAddAccountOpen(true); }}
            onSignatures={() => setSignaturesOpen(true)}
            onDeleteAccountOpen={() => setDeleteAccountOpen(true)}
            onLogout={handleLogout}
            notifiche={notifiche}
            onToggleNotifiche={toggleNotifiche}
            unread={unread}
            backupAllowed={backupAllowed}
            onBackup={handleBackup}
            isAdmin={user?.role === "admin"}
            onAdmin={() => setAdminOpen(true)}
            stato={statoCaselle}
            onCheckStato={checkStato}
            onInstall={handleInstall}
            installabile={installabile}
            onDiagnostica={() => setErroriOpen(true)}
            onManutenzione={() => setManutenzioneOpen(true)}
            onRelay={() => setRelayOpen(true)}
            soloNonLette={soloNonLette}
            onToggleNonLette={() => setSoloNonLette((v) => !v)}
            socialInArrivo={socialInArrivo}
            onToggleSocialInArrivo={() => setSocialInArrivo((v) => !v)}
          />

          <QuickBar
            refreshing={loadingMail}
            onVideoCall={() => setVideoCallOpen(true)}
            onCalendar={() => setCalendarioOpen(true)}
          />
          <MemoBanner memo={memo} onChange={caricaMemo} onApri={() => setCalendarioOpen(true)} onOpenEmail={apriEmailId} />
          <SyncBar status={syncStatus} />
          {query.trim().length >= 2 && (
            <div className="server-search" data-testid="server-search-bar">
              <div className="search-pec" data-testid="search-pec">
                <button className={`search-chip ${searchPec ? "on" : ""}`} onClick={() => setSearchPec((v) => !v)} data-testid="search-pec-toggle">
                  Solo PEC
                </button>
                {searchPec &&
                  [["", "Tutte"], ["consegna", "Consegna"], ["accettazione", "Accettazione"], ["anomalia", "Anomalia"], ["certificata", "Posta certificata"]].map(([v, l]) => (
                    <button key={v || "tutte"} className={`search-chip small ${searchPecTipo === v ? "on" : ""}`} onClick={() => setSearchPecTipo(v)} data-testid={`search-pec-${v || "tutte"}`}>
                      {l}
                    </button>
                  ))}
              </div>
              <span className="server-search-info" data-testid="server-search-info">
                {serverSearch.stato === "in_corso" && <Loader2 size={13} className="spin" />}
                {testoRicerca(filtered.length, ricercaPronta, serverSearch)}
              </span>
            </div>
          )}

          <EmailList
            emails={filtered}
            ricevuteDi={ricevuteDi}
            termini={query.trim().length >= 2 ? query.trim().split(/\s+/).filter(Boolean) : null}
            loading={loadingMail}
            onEmptyTrash={folder === "eliminate" && filtered.length > 0 ? () => setConfermaSvuota(true) : null}
            hasMore={query.trim().length >= 2 ? serverSearch.hasMore : hasMore && (folder === "ricevute" || folder === "inviate")}
            loadingMore={query.trim().length >= 2 ? loadingRisultati : loadingMore}
            onLoadMore={caricaAltri}
            selectedId={selectedId}
            onSelect={handleSelect}
            onPrefetch={prefetchHover}
            onOpenFull={(id) => {
              if (id !== selectedId) handleSelect(id);
              setFullscreen(true);
            }}
            onSwipeUnread={conRiga(handleSwipeUnread)}
            onSwipeArchive={conRiga(handleSwipeArchive)}
            onSwipeDelete={conRiga(handleSwipeDelete)}
            onLongPress={conRiga((em) => setCtxEmail(em))}
            selectMode={selectMode}
            checkedIds={checkedIds}
            onToggleCheck={toggleCheck}
          />

          {selectMode && (
            <div className="select-bar" data-testid="select-bar">
              <span>{checkedIds.length} selezionate</span>
              <button
                className="wf-btn select-all-btn"
                onClick={() => setCheckedIds(filtered.map((e) => e.id))}
                disabled={checkedIds.length === filtered.length}
                data-testid="select-all"
              >
                Tutte
              </button>
              <div style={{ flex: 1 }} />
              <button
                className="icon-btn"
                onClick={deleteChecked}
                disabled={checkedIds.length === 0}
                aria-label="Elimina selezionate"
                data-testid="select-delete"
              >
                <Trash2 size={20} strokeWidth={2.3} />
              </button>
              <button className="icon-btn" onClick={exitSelectMode} aria-label="Annulla selezione" data-testid="select-cancel">
                <X size={20} strokeWidth={2.6} />
              </button>
            </div>
          )}
        </div>

        <div className="pane pane-detail">
          <TopBar
            mode="detail"
            title={selected?.oggetto}
            isPec={!!(selected && (selected.categoria === "pec" || selected.pec_tipo))}
            onBack={() => setSelectedId(null)}
            onTranslate={selected ? detailToolbar.onTranslate : null}
            onAccept={handleAcceptMail}
            onReject={handleRejectMail}
            onCompose={() => {
              setComposeInitial(null);
              setComposeOpen(true);
            }}
          />
          <EmailDetail
            email={selected}
            ricevute={selected ? ricevuteDi[selected.id] : null}
            onRetryOutbox={retryOutbox}
            onDeleteOutbox={handleDelete}
            onQuickReply={quickReply}
            onRicordamelo={(e) => setMemoDaEmail(e)}
            onScriviA={(indirizzo) => {
              setComposeInitial({ account_id: selected?.account, a: indirizzo, cc: "", ccn: "", oggetto: "", corpo: "" });
              setComposeOpen(true);
            }}
            onMeet={(e) => { setVideoCallInit({ titolo: e.oggetto, invitati: e.email, mittente: e.mittente, mittente_email: e.email, account_id: e.account }); setVideoCallOpen(true); }}
          />
          <BottomToolbar ns="detail" {...toolbarCommon} {...detailToolbar} />
        </div>
      </div>

      {confermaSvuota && (
        <div className="wf-modal-backdrop show compose-confirm-backdrop" onClick={() => setConfermaSvuota(false)}>
          <div className="wf-modal compose-confirm" onClick={(e) => e.stopPropagation()} data-testid="empty-trash-confirm">
            <div className="wf-modal-head">
              <span className="wf-modal-title">Svuotare Eliminate?</span>
            </div>
            <div className="compose-confirm-text">
              Le {filtered.length} email nella cartella Eliminate verranno cancellate definitivamente, anche dal server di posta. L'operazione non si può annullare.
            </div>
            <div className="wf-modal-actions compose-confirm-actions">
              <button className="wf-btn" onClick={() => setConfermaSvuota(false)} data-testid="empty-trash-cancel">Annulla</button>
              <button className="wf-btn del-profile-btn" onClick={svuotaEliminate} data-testid="empty-trash-confirm-btn">Svuota definitivamente</button>
            </div>
          </div>
        </div>
      )}

      {fullscreen && selected && (
        <FullscreenEmail
          email={selected}
          ricevute={ricevuteDi[selected.id]}
          onClose={chiudiFullscreen}
          onRetryOutbox={retryOutbox}
          onDeleteOutbox={handleDelete}
          onQuickReply={quickReply}
          onTranslate={detailToolbar.onTranslate}
        >
          {openMenu && <Oscuramento menu={openMenu} onClick={() => setOpenMenu(null)} testid="fs-dd-backdrop" />}
          <BottomToolbar ns="fs" {...toolbarCommon} {...detailToolbar} />
        </FullscreenEmail>
      )}

      <CalendarioModal open={calendarioOpen} onClose={() => setCalendarioOpen(false)} emails={emails} onOpenEmail={handleSelect} memo={memo} accounts={customAccounts} onMemoChange={caricaMemo} />
      <MemoRapidoModal email={memoDaEmail} onClose={() => setMemoDaEmail(null)} onSaved={caricaMemo} />
      <VideoCallModal
        open={videoCallOpen}
        initial={videoCallInit}
        onMemoChange={caricaMemo}
        onClose={() => { setVideoCallOpen(false); setVideoCallInit(null); }}
        onInvia={(init) => {
          setVideoCallOpen(false);
          setComposeInitial(init);
          setComposeOpen(true);
        }}
      />

      <ComposeModal
        open={composeOpen}
        onClose={(opts) => {
          setComposeOpen(false);
          setComposeInitial(null);
          if (opts?.scartaBozza) {
            api.deleteDraft(opts.scartaBozza).catch(() => {});
            setEmails((prev) => prev.filter((e) => e.id !== "draft:" + opts.scartaBozza));
          }
        }}
        onSend={handleSend}
        onAutosave={(payload) =>
          api.saveDraft(payload).then((b) => {
            setEmails((prev) => [...draftRows([b]), ...prev.filter((e) => e.id !== "draft:" + b.id)]);
            return b;
          })
        }
        initial={composeInitial}
        accounts={customAccounts}
        defaultAccountId={selected?.account || accountFilter}
      />
      <AIPanel
        open={aiOpen}
        onClose={() => {
          setAiOpen(false);
          setAiMode(null);
        }}
        email={selected}
        initialMode={aiMode}
      />
      <AddAccountModal
        open={addAccountOpen}
        preselect={addAccountPre}
        onClose={() => { setAddAccountOpen(false); setAddAccountPre(null); }}
        onAdd={handleAddAccount}
      />
      <SignatureModal
        open={signaturesOpen}
        onClose={() => setSignaturesOpen(false)}
        accounts={mailAccounts}
        onSaved={(id, firma) =>
          setCustomAccounts((prev) => prev.map((a) => (a.id === id ? { ...a, ...firma } : a)))
        }
      />
      <DeleteAccountModal
        open={deleteAccountOpen}
        onClose={() => setDeleteAccountOpen(false)}
        accounts={customAccounts}
        onDeleteAccount={handleDeleteAccount}
        onAccountUpdated={(acc) => setCustomAccounts((prev) => prev.map((a) => (a.id === acc.id ? { ...a, ...acc } : a)))}
        user={user}
        onDeleteProfile={handleDeleteProfile}
      />
      <AdminModal open={adminOpen} onClose={() => setAdminOpen(false)} me={user} />
      <RelayModal open={relayOpen} onClose={() => setRelayOpen(false)} accounts={mailAccounts} onSaved={() => api.getAccounts().then(setCustomAccounts).catch(() => {})} />
      <ManutenzioneModal open={manutenzioneOpen} onClose={() => setManutenzioneOpen(false)} />
      <ErroriModal open={erroriOpen} onClose={() => setErroriOpen(false)} versione={appVersion} serverHost={BACKEND_HOST} />
      <ContextMenu email={ctxEmail} onClose={() => setCtxEmail(null)} onAction={handleCtxAction} inArrivoPossibile={!!ctxEmail && ctxEmail.folder === "ricevute" && !ctxEmail.in_arrivo && !!socialNetwork(ctxEmail)} />
      </>
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <VikiLoader />
      <Routes>
        <Route path="/" element={<MailApp />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
